import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Stack
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../api/client';
import ContratosDashboardCards from '../components/contratos/ContratosDashboardCards';
import ContratosModuloHeader from '../components/contratos/ContratosModuloHeader';
import SlaSolicitacoesChart from '../components/contratos/SlaSolicitacoesChart';
import FaturamentoMensalChart from '../components/contratos/FaturamentoMensalChart';
import {
  buildContratosDashboardQuery,
  isUsuarioFiscalRestrito,
  isUsuarioSetorContratos
} from '../utils/contratosDashboardUtils';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';

async function fetchDashboardJson(path, queryString) {
  const url = queryString ? `${path}?${queryString}` : path;
  const res = await apiFetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erro ${res.status}`);
  return data;
}

export default function ContratosDashboardPage() {
  const {
    unidadeSelecionada,
    user,
    isAdmin,
    isFiscal,
    fiscalSetor,
    rbacRole,
    isCoordenadorFinanceiro,
    isAnalistaFinanceiro,
  } = useAuth();
  const [setoresOpcoes, setSetoresOpcoes] = useState([]);
  const [setoresSelecionados, setSetoresSelecionados] = useState([]);
  const [slaDados, setSlaDados] = useState(null);
  const [faturamentoDados, setFaturamentoDados] = useState(null);
  const [loadingGraficos, setLoadingGraficos] = useState(true);

  const isSetorContratos = useMemo(
    () => isUsuarioSetorContratos({ user, isAdmin, isFiscal: isFiscal && rbacRole === 'FISCAL' }),
    [user, isAdmin, isFiscal, rbacRole]
  );
  const isFiscalRestrito = useMemo(
    () =>
      isUsuarioFiscalRestrito({
        isFiscal,
        isSetorContratos,
        rbacRole,
        isCoordenadorFinanceiro,
        isAnalistaFinanceiro,
      }),
    [isFiscal, isSetorContratos, rbacRole, isCoordenadorFinanceiro, isAnalistaFinanceiro]
  );

  const filtroDashboard = useMemo(() => {
    const base = { unidade: unidadeSelecionada?.codigo || undefined };
    if (isFiscalRestrito) {
      return { ...base, setorLogado: fiscalSetor || user?.setor || undefined };
    }
    if (isSetorContratos && setoresSelecionados.length > 0) {
      return { ...base, setoresSelecionados };
    }
    return base;
  }, [unidadeSelecionada?.codigo, isFiscalRestrito, isSetorContratos, setoresSelecionados, fiscalSetor, user?.setor]);

  useEffect(() => {
    if (!isSetorContratos) return;
    apiFetch('/api/setores')
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setSetoresOpcoes(Array.isArray(rows) ? rows : []))
      .catch(() => setSetoresOpcoes([]));
  }, [isSetorContratos]);

  const carregarGraficos = useCallback(async () => {
    setLoadingGraficos(true);
    try {
      const qs = buildContratosDashboardQuery({ ...filtroDashboard, ano: new Date().getFullYear() });
      const [sla, fat] = await Promise.all([
        fetchDashboardJson('/api/contratos/dashboard-sla-solicitacoes', qs),
        fetchDashboardJson('/api/contratos/dashboard-faturamento-mensal', qs)
      ]);
      setSlaDados(sla);
      setFaturamentoDados(fat);
    } catch (e) {
      console.error(e);
      setSlaDados(null);
      setFaturamentoDados(null);
    } finally {
      setLoadingGraficos(false);
    }
  }, [filtroDashboard]);

  useEffect(() => {
    carregarGraficos();
  }, [carregarGraficos]);

  const subtituloPerfil = isFiscalRestrito
    ? `Visão restrita ao setor ${fiscalSetor || user?.setor || 'do fiscal'}.`
    : isSetorContratos
      ? 'Visão global do setor de contratos. Use o filtro abaixo para segmentar por setor.'
      : 'Visão consolidada dos principais indicadores do ciclo de contratos.';

  return (
    <Box sx={{ p: 2 }}>
      <VisaoRestritaBanner />
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.08) 100%)',
          border: '1px solid rgba(25,118,210,0.15)'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary">
              Dashboard de Contratos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {subtituloPerfil}
              {unidadeSelecionada?.nome ? ` Unidade: ${unidadeSelecionada.nome}.` : ' Exibindo dados gerais.'}
            </Typography>
          </Box>
          <ContratosModuloHeader />
        </Box>
      </Paper>

      {isSetorContratos && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            border: '1px solid rgba(21,101,192,0.15)',
            background: 'linear-gradient(135deg, rgba(227,242,253,0.45) 0%, rgba(255,255,255,0.92) 100%)'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700} color="primary">
              Filtro por setores
            </Typography>
            {setoresSelecionados.length > 0 && (
              <Chip size="small" label={`${setoresSelecionados.length} selecionado(s)`} color="primary" variant="outlined" />
            )}
          </Stack>
          <FormControl fullWidth size="small" sx={{ maxWidth: 560 }}>
            <InputLabel>Setores (opcional — vazio = todos)</InputLabel>
            <Select
              multiple
              value={setoresSelecionados}
              label="Setores (opcional — vazio = todos)"
              onChange={(e) => setSetoresSelecionados(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => (selected.length ? selected.join(', ') : 'Todos os setores')}
            >
              {setoresOpcoes.map((s) => (
                <MenuItem key={s.id} value={s.nome}>
                  <Checkbox checked={setoresSelecionados.indexOf(s.nome) > -1} />
                  <ListItemText primary={s.nome} secondary={s.descricao || undefined} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      <ContratosDashboardCards filtroDashboard={filtroDashboard} />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <SlaSolicitacoesChart dados={slaDados} loading={loadingGraficos} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FaturamentoMensalChart dados={faturamentoDados} loading={loadingGraficos} />
        </Grid>
      </Grid>
    </Box>
  );
}
